import React, { Component } from "react";
import ReactApexChart from "react-apexcharts";
import { withApollo } from "react-apollo";
import gql from "graphql-tag";
import * as Cookies from "es-cookie";
import ScopeFilters from "./ScopeFilters";
import ExportButtons from "./ExportButtons";
import { exportExcel, exportPdf } from "./misExport";
import { TableShimmer } from "../preloader/PageCardShimmer";
import { headerBranches } from "./misScope";

const GET_RISK = gql`
  query($institute_id: Int!, $branch_id: String, $class_id: String, $section_id: String, $stream_id: String) {
    getAtRiskStudents(institute_id: $institute_id, branch_id: $branch_id, class_id: $class_id, section_id: $section_id, stream_id: $stream_id) {
      kpis { highRisk mediumRisk decliningRank attendanceLow }
      register { name id class score avg reasons }
      distribution { high medium onTrack }
      alerts { name text ago }
      attendanceRisk { name id class pct present marked }
    }
  }
`;

const toCsv = (arr) => (arr && arr.length ? arr.join(",") : null);
const inSel = (arr, val) => !arr || !arr.length || arr.some((x) => String(x) === String(val));
const effectiveBranchCsv = (scope, sel) => {
  // Branch is chosen ONCE in the header (NavbarOne) and applies to every tab; State/City
  // only narrow within that selection. See utils/branchScope + misScope.headerBranches.
  const scoped = headerBranches(scope).filter((b) => inSel(sel.state, b.state_id) && inSel(sel.city, b.city_id));
  return toCsv(scoped.map((b) => b.id));
};
const EMPTY_RISK = { kpis: {}, distribution: null, register: [], alerts: [], attendanceRisk: [] };

/* ──────────────────────────────────────────────────────────────────────────
   At-Risk & Early Warning System — Management MIS Reports tab.
   API-READY: data comes from props and defaults to empty. Wire a real query
   (e.g. getAtRiskStudents) and pass the results as props.

   Expected props (all optional):
     kpis: { highRisk, mediumRisk, decliningRank, attendanceLow }
     register: [{ name, id, class, score, reasons: [..] }]
     distribution: { high, medium, onTrack }
     alerts: [{ name, text, ago }]
     escalation: [{ day, color, title, desc, meta }]   // defaults to the standard flow
   ────────────────────────────────────────────────────────────────────────── */

const KPI_META = [
  { key: "highRisk", label: "High Risk", sub: "urgent", icon: "fal fa-bell", color: "#dc2626" },
  { key: "mediumRisk", label: "Medium Risk", sub: "watchlist", icon: "fal fa-exclamation-triangle", color: "#f59e0b" },
  { key: "attendanceLow", label: "Attendance < 75%", sub: "early warning", icon: "fal fa-user-clock", color: "#0ea5e9" },
];

const AVATAR_COLORS = ["#f59e0b", "#6366f1", "#8b5cf6", "#10b981", "#ec4899", "#3b82f6", "#ef4444", "#14b8a6", "#f97316"];
const initials = (name) => (name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
const colorFor = (s) => AVATAR_COLORS[((s || "?").charCodeAt(0) + (s || "").length) % AVATAR_COLORS.length];

const S = {
  page: { background: "#f1f5f9", padding: "20px 24px 60px" },
  hRow: { display: "flex", alignItems: "flex-start", justifyContent: "space-between", flexWrap: "wrap", gap: 12, marginBottom: 16 },
  crumb: { fontSize: 12, color: "#94a3b8", marginBottom: 4 },
  h1: { fontSize: 24, fontWeight: 800, color: "#0f172a", margin: 0 },
  sub: { fontSize: 13, color: "#64748b", marginTop: 4 },
  btnGhost: { background: "#fff", border: "1px solid #e2e8f0", borderRadius: 9, padding: "8px 14px", fontSize: 13, fontWeight: 600, color: "#334155", cursor: "pointer" },
  btnPrimary: { background: "#4f46e5", border: "none", borderRadius: 9, padding: "8px 16px", fontSize: 13, fontWeight: 600, color: "#fff", cursor: "pointer" },
  card: { background: "#fff", borderRadius: 14, border: "1px solid #e2e8f0", boxShadow: "0 1px 3px rgba(0,0,0,0.04)" },
  cardPad: { padding: "18px 20px" },
  cardTitle: { fontSize: 15, fontWeight: 700, color: "#0f172a", marginBottom: 14 },
  kpiGrid: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))", gap: 16, marginBottom: 16 },
  kpiCard: { display: "flex", alignItems: "center", justifyContent: "space-between", padding: "18px 20px" },
  kpiLabel: { fontSize: 13, color: "#64748b", fontWeight: 600 },
  kpiValue: { fontSize: 30, fontWeight: 800, color: "#0f172a", lineHeight: 1.1, margin: "4px 0" },
  kpiSub: { fontSize: 12, color: "#dc2626", fontWeight: 600 },
  kpiIcon: (c) => ({ width: 46, height: 46, borderRadius: 12, background: c + "1a", color: c, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, flexShrink: 0 }),
  grid: { display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 16, marginBottom: 16, alignItems: "start" },
  grid2: { display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(380px, 1fr))", gap: 16 },
  th: { textAlign: "left", fontSize: 11, fontWeight: 700, color: "#64748b", textTransform: "uppercase", padding: "8px 10px", borderBottom: "2px solid #e2e8f0" },
  td: { fontSize: 13, color: "#334155", padding: "10px", borderBottom: "1px solid #f1f5f9", verticalAlign: "top" },
  avatar: (c) => ({ width: 30, height: 30, borderRadius: 8, background: c, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, fontWeight: 700, marginRight: 8, flexShrink: 0 }),
  reasonPill: { background: "#fef2f2", color: "#dc2626", borderRadius: 16, padding: "3px 10px", fontSize: 11, fontWeight: 600, display: "inline-block", marginBottom: 4 },
  riskPill: { background: "#fee2e2", color: "#dc2626", borderRadius: 16, padding: "4px 10px", fontSize: 11, fontWeight: 700, whiteSpace: "nowrap" },
  badge: { background: "#ecfdf5", color: "#059669", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 700, marginLeft: 8 },
  banner: { background: "#fffbeb", border: "1px solid #fde68a", borderRadius: 10, padding: "12px 14px", fontSize: 12.5, color: "#92400e", marginTop: 16 },
  empty: { padding: "28px 10px", textAlign: "center", color: "#94a3b8", fontSize: 13 },
  // Sample/dummy sections are rendered muted + grayscale to signal they aren't live.
  dummy: { filter: "grayscale(1)", opacity: 0.55 },
  sampleTag: { fontSize: 10, fontWeight: 700, color: "#94a3b8", background: "#f1f5f9", borderRadius: 6, padding: "2px 8px", marginLeft: 8, verticalAlign: "middle", textTransform: "uppercase", letterSpacing: 0.4 },
};

const GRAY = "#94a3b8";

class AtRiskEarlyWarningTab extends Component {
  state = { sel: { state: [], city: [], branch: [], class: [], stream: [], section: [] }, risk: null, loading: true, drillClass: null, drillAttClass: null };
  setSel = (nextSel) => this.setState({ sel: nextSel }, this.load);

  componentDidMount() { this.defaultSel(); this.load(); }

  // The header's branch selector hands down a NEW scope object; re-default and refetch when it
  // changes. defaultSel() returns early once it has run, so without clearing the flag this tab
  // kept showing whatever branch was selected on first load.
  componentDidUpdate(prevProps) {
    if (prevProps.scope !== this.props.scope) this._defaulted = false;
    this.defaultSel();
  }

  defaultSel = () => {
    if (this._defaulted) return;
    const sc = this.props.scope || {};
    if (!((sc.branches || []).length || (sc.classes || []).length || (sc.sections || []).length)) return;
    this._defaulted = true;
    this.setState({
      sel: {
        state: (sc.states || []).map((s) => s.state_id),
        city: (sc.cities || []).map((c) => c.id),
        branch: (sc.branches || []).map((b) => b.id),
        class: [...new Set((sc.classes || []).map((c) => c.id))],
        stream: [...new Set((sc.streams || []).map((s) => s.id))],
        section: (sc.sections || []).map((s) => s.id),
      },
    }, this.load);
  };

  load = async () => {
    const instId = parseInt(Cookies.get("institutionid")) || 118;
    const { sel } = this.state;
    this.setState({ loading: true });
    try {
      const res = await this.props.client.query({
        query: GET_RISK,
        variables: { institute_id: instId, branch_id: effectiveBranchCsv(this.props.scope || {}, sel), class_id: toCsv(sel.class), section_id: toCsv(sel.section), stream_id: toCsv(sel.stream) },
        fetchPolicy: "network-only",
      });
      this.setState({ risk: (res.data && res.data.getAtRiskStudents) || EMPTY_RISK, loading: false });
    } catch (e) { console.error("at-risk", e); this.setState({ risk: EMPTY_RISK, loading: false }); }
  };

  // Build the two report tables (register + attendance < 75%) for export.
  exportTables = () => {
    const d = this.state.risk || EMPTY_RISK;
    const register = d.register || [];
    const att = d.attendanceRisk || [];
    return [
      {
        heading: `At-Risk Register (${register.length})`,
        columns: ["Student", "ID", "Class", "Avg %", "Reasons"],
        rows: register.map((s) => [s.name, s.id, s.class, s.avg == null ? "—" : `${s.avg}%`, (s.reasons || []).join("; ")]),
      },
      {
        heading: `Attendance < 75% (${att.length})`,
        columns: ["Student", "ID", "Class", "Attendance %", "Present", "Marked"],
        rows: att.map((s) => [s.name, s.id, s.class, `${s.pct}%`, s.present, s.marked]),
      },
    ];
  };

  handleExcel = () => {
    const t = this.exportTables();
    exportExcel(t.map((x) => ({ name: x.heading.replace(/\s*\(\d+\)$/, ""), aoa: [x.columns, ...x.rows] })), "at_risk_early_warning.xlsx");
  };

  handlePdf = () => {
    exportPdf("At-Risk & Early Warning System", this.exportTables(), "at_risk_early_warning.pdf");
  };

  render() {
    const data = this.state.risk || EMPTY_RISK;
    const {
      kpis = {},
      register = [],
      distribution = null,
      alerts = [],
      attendanceRisk = [],
    } = data;

    const donutOptions = {
      chart: { type: "donut", fontFamily: "inherit" },
      labels: ["High", "Medium", "On-track"],
      colors: ["#ef4444", "#f59e0b", "#16a34a"],
      legend: { position: "top", markers: { radius: 12 } },
      dataLabels: { enabled: false },
      plotOptions: { pie: { donut: { size: "62%" } } },
      stroke: { width: 2 },
      noData: { text: "No data" },
    };
    const donutSeries = distribution
      ? [distribution.high || 0, distribution.medium || 0, distribution.onTrack || 0]
      : [];

    // Risk by class — count of high-risk students per class (from the register).
    const byClass = {};
    register.forEach((s) => { const k = s.class || "Unassigned"; byClass[k] = (byClass[k] || 0) + 1; });
    const riskClassCats = Object.keys(byClass);
    const riskByClassOptions = {
      chart: {
        type: "bar", toolbar: { show: false }, fontFamily: "inherit",
        // Click a bar -> open the drilldown modal with that class's students.
        events: {
          dataPointSelection: (e, ctx, cfg) => {
            const cls = riskClassCats[cfg.dataPointIndex];
            if (cls != null) this.setState({ drillClass: cls });
          },
        },
      },
      plotOptions: { bar: { borderRadius: 4, columnWidth: "45%", distributed: true } },
      colors: ["#ef4444", "#f59e0b", "#dc2626", "#fb923c", "#7c3aed", "#3b82f6"],
      dataLabels: { enabled: true },
      legend: { show: false },
      grid: { borderColor: "#f1f5f9" },
      xaxis: { categories: riskClassCats, labels: { style: { fontSize: "11px" } } },
      states: { active: { filter: { type: "none" } } },
      noData: { text: "No data" },
    };
    const drillClass = this.state.drillClass;
    const drillStudents = drillClass != null ? register.filter((s) => (s.class || "Unassigned") === drillClass) : [];
    const riskByClassSeries = [{ name: "High-risk students", data: riskClassCats.map((k) => byClass[k]) }];

    // Attendance < 75% by class — count per class (from attendanceRisk).
    const attByClass = {};
    attendanceRisk.forEach((s) => { const k = s.class || "Unassigned"; attByClass[k] = (attByClass[k] || 0) + 1; });
    const attClassCats = Object.keys(attByClass);
    const attByClassOptions = {
      chart: {
        type: "bar", toolbar: { show: false }, fontFamily: "inherit",
        events: {
          dataPointSelection: (e, ctx, cfg) => {
            const cls = attClassCats[cfg.dataPointIndex];
            if (cls != null) this.setState({ drillAttClass: cls });
          },
        },
      },
      plotOptions: { bar: { borderRadius: 4, columnWidth: "45%", distributed: true } },
      colors: ["#0ea5e9", "#0284c7", "#38bdf8", "#0369a1", "#7dd3fc", "#0891b2"],
      dataLabels: { enabled: true },
      legend: { show: false },
      grid: { borderColor: "#f1f5f9" },
      xaxis: { categories: attClassCats, labels: { style: { fontSize: "11px" } } },
      states: { active: { filter: { type: "none" } } },
      noData: { text: "No data" },
    };
    const attByClassSeries = [{ name: "Students < 75%", data: attClassCats.map((k) => attByClass[k]) }];
    const drillAttClass = this.state.drillAttClass;
    const drillAttStudents = drillAttClass != null ? attendanceRisk.filter((s) => (s.class || "Unassigned") === drillAttClass) : [];

    return (
      <div style={S.page}>
        {/* Header */}
        <div style={S.hRow}>
          <div>
            <div style={S.crumb}>At-Risk &amp; Early Warning System</div>
            <h1 style={S.h1}>At-Risk &amp; Early Warning System {this.state.loading && <i className="fal fa-spinner fa-spin" style={{ fontSize: 16, color: "#dc2626", marginLeft: 8 }} />}</h1>
            <div style={S.sub}>Automated dropout/failure risk detection with escalation</div>
          </div>
          <ExportButtons onExcel={this.handleExcel} onPdf={this.handlePdf} disabled={this.state.loading} />
        </div>

        {/* Scope filters — cascading State -> City -> Branch -> Class -> Section */}
        <ScopeFilters scope={this.props.scope} sel={this.state.sel} onChange={this.setSel}
          style={{ ...S.card, display: "flex", alignItems: "flex-end", gap: 12, flexWrap: "wrap", padding: "12px 16px", marginBottom: 16 }} />

        {/* KPI cards */}
        <div style={S.kpiGrid}>
          {KPI_META.map((k) => (
            <div key={k.label} style={{ ...S.card, ...S.kpiCard }}>
              <div>
                <div style={S.kpiLabel}>{k.label}</div>
                <div style={S.kpiValue}>{kpis[k.key] != null ? kpis[k.key] : "—"}</div>
                <div style={S.kpiSub}>{k.sub}</div>
              </div>
              <div style={S.kpiIcon(k.color)}><i className={k.icon} /></div>
            </div>
          ))}
        </div>

        {/* Register + Risk distribution */}
        <div style={S.grid}>
          <div style={{ ...S.card, ...S.cardPad }}>
            <div style={S.cardTitle}>At-Risk Register — reasons &amp; risk <span style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>({register.length})</span></div>
            {this.state.loading ? <TableShimmer rows={6} cols={4} /> : (
            <div style={{ overflowX: "auto", maxHeight: 420, overflowY: "auto" }}>
              <table style={{ width: "100%", minWidth: 520 }}>
                <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
                  <tr>
                    <th style={S.th}>Student</th><th style={S.th}>Class</th>
                    <th style={S.th}>Reasons</th><th style={S.th}>Risk</th>
                  </tr>
                </thead>
                <tbody>
                  {register.length === 0 ? (
                    <tr><td colSpan={4} style={S.empty}>No data</td></tr>
                  ) : register.map((s) => (
                    <tr key={s.id}>
                      <td style={S.td}>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span style={S.avatar(colorFor(s.name))}>{initials(s.name)}</span>
                          <span>
                            <span style={{ fontWeight: 600 }}>{s.name}</span><br />
                            <span style={{ fontSize: 11, color: "#94a3b8" }}>{s.id}</span>
                          </span>
                        </div>
                      </td>
                      <td style={S.td}>{s.class}</td>
                      <td style={S.td}>
                        {(s.reasons || []).map((r) => <span key={r} style={{ ...S.reasonPill, marginRight: 4 }}>{r}</span>)}
                      </td>
                      <td style={S.td}><span style={S.riskPill}>● High Risk</span></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            )}
          </div>

          <div style={{ ...S.card, ...S.cardPad }}>
            <div style={S.cardTitle}>Risk Distribution</div>
            <ReactApexChart options={donutOptions} series={donutSeries} type="donut" height={280} />
            {distribution && distribution.high != null ? (
              <div style={S.banner}>
                <i className="fal fa-exclamation-triangle" /> <b>{distribution.high} students</b> crossed into high-risk this week.
                Auto-alerts sent to faculty &amp; coordinators.
              </div>
            ) : null}
          </div>
        </div>

        {/* Risk by Class + Attendance < 75% by Class */}
        <div style={S.grid2}>
          <div style={{ ...S.card, ...S.cardPad }}>
            <div style={S.cardTitle}>High-risk Students by Class <span style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>(click a bar for students)</span></div>
            <ReactApexChart options={riskByClassOptions} series={riskByClassSeries} type="bar" height={300} />
          </div>
          <div style={{ ...S.card, ...S.cardPad }}>
            <div style={S.cardTitle}>Attendance &lt; 75% by Class <span style={{ fontSize: 11, fontWeight: 500, color: "#94a3b8" }}>(click a bar for students)</span></div>
            <ReactApexChart options={attByClassOptions} series={attByClassSeries} type="bar" height={300} />
          </div>
        </div>

        {/* Drilldown: high-risk students for the clicked class */}
        {drillClass != null && (
          <div onClick={() => this.setState({ drillClass: null })}
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 560, maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #eef2f7" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>
                  <i className="fal fa-triangle-exclamation" style={{ color: "#dc2626", marginRight: 8 }} />
                  High-risk Students — {drillClass} <span style={{ fontSize: 12, color: "#94a3b8" }}>({drillStudents.length})</span>
                </div>
                <button onClick={() => this.setState({ drillClass: null })} style={{ border: "none", background: "none", fontSize: 20, color: "#64748b", cursor: "pointer", lineHeight: 1 }}>&times;</button>
              </div>
              <div style={{ overflowY: "auto", padding: "6px 0" }}>
                {drillStudents.length === 0 ? (
                  <div style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>No students.</div>
                ) : drillStudents.map((s) => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 18px", borderBottom: "1px solid #f5f7fa" }}>
                    <span style={S.avatar(colorFor(s.name))}>{initials(s.name)}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 600, color: "#1f2937" }}>{s.name}</span><br />
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>{s.id}</span>
                    </span>
                    <span style={{ fontWeight: 800, fontSize: 13, color: "#dc2626", minWidth: 44, textAlign: "right" }}>{s.avg == null ? "—" : `${s.avg}%`}</span>
                    <span style={{ display: "flex", flexWrap: "wrap", gap: 4, justifyContent: "flex-end" }}>
                      {(s.reasons || []).map((r) => <span key={r} style={S.reasonPill}>{r}</span>)}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {/* Drilldown: attendance < 75% students for the clicked class */}
        {drillAttClass != null && (
          <div onClick={() => this.setState({ drillAttClass: null })}
            style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.45)", zIndex: 1050, display: "flex", alignItems: "center", justifyContent: "center", padding: 16 }}>
            <div onClick={(e) => e.stopPropagation()}
              style={{ background: "#fff", borderRadius: 14, width: "100%", maxWidth: 560, maxHeight: "80vh", display: "flex", flexDirection: "column", boxShadow: "0 20px 60px rgba(0,0,0,0.25)" }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "14px 18px", borderBottom: "1px solid #eef2f7" }}>
                <div style={{ fontWeight: 700, color: "#0f172a" }}>
                  <i className="fal fa-user-clock" style={{ color: "#0ea5e9", marginRight: 8 }} />
                  Attendance &lt; 75% — {drillAttClass} <span style={{ fontSize: 12, color: "#94a3b8" }}>({drillAttStudents.length})</span>
                </div>
                <button onClick={() => this.setState({ drillAttClass: null })} style={{ border: "none", background: "none", fontSize: 20, color: "#64748b", cursor: "pointer", lineHeight: 1 }}>&times;</button>
              </div>
              <div style={{ overflowY: "auto", padding: "6px 0" }}>
                {drillAttStudents.length === 0 ? (
                  <div style={{ padding: 24, textAlign: "center", color: "#94a3b8" }}>No students.</div>
                ) : drillAttStudents.map((s) => (
                  <div key={s.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "9px 18px", borderBottom: "1px solid #f5f7fa" }}>
                    <span style={S.avatar(colorFor(s.name))}>{initials(s.name)}</span>
                    <span style={{ flex: 1, minWidth: 0 }}>
                      <span style={{ fontWeight: 600, color: "#1f2937" }}>{s.name}</span><br />
                      <span style={{ fontSize: 11, color: "#94a3b8" }}>{s.id}</span>
                    </span>
                    <span style={{ fontSize: 11, color: "#94a3b8", minWidth: 70, textAlign: "right" }}>{s.present}/{s.marked} days</span>
                    <span style={{ fontWeight: 800, fontSize: 13, color: "#0284c7", minWidth: 44, textAlign: "right" }}>{s.pct}%</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    );
  }
}

export default withApollo(AtRiskEarlyWarningTab);
